import { brush, footprint, inkStroke, leaf, sprite, star, waves } from '../kit';
import { C, type Pix } from '../pix';
import { registerIcons } from '../store';

/**
 * กระบี่ปลายแท่งหมึก (หอหมึกสน, soft): the recorder's finger-sword. A pointing hand, thin lines of
 * sword qi, ink and paper, pine needles and a qin's strings; cool pine-blue light, ink on paper.
 */

const INK = '#1e1a24';
const QI = '#bff0ff';
const QI_DK = '#5cb8d8';
const PINE = '#4a9a6a';
const PAPER = C.paper;
const SKIN = C.skin;

/** A hand pointing right with the index (and middle) finger (13×7). */
function pointing(p: Pix, x: number, y: number, two = false): void {
  sprite(
    p,
    x,
    y,
    two
      ? ['......sssssss', '.......ssssss', 'ssssssssss...', 'sssssssss....', 'ssssssss.....', '.ssssss......']
      : ['.......ssssss', 'ssssssss.....', 'sssssssss....', 'ssssssss.....', 'ssssssss.....', '.ssssss......'],
    { s: SKIN },
  );
}

/** A thin straight line of sword qi with a pale core. */
function qiLine(p: Pix, x0: number, y0: number, x1: number, y1: number): void {
  p.thick(x0, y0, x1, y1, QI_DK, 2);
  p.line(x0, y0, x1, y1, QI);
}

/** A pressure point: a ring with a bright core. */
function point(p: Pix, cx: number, cy: number, r = 3): void {
  p.ring(cx, cy, r, r, QI_DK, 0, Math.PI * 2);
  p.rect(cx - 1, cy - 1, 2, 2, QI);
}

registerIcons('skill', {
  // Finger-sword: a line of qi shot from the fingertip at a far point.
  'sf-finger': (p) => {
    pointing(p, 1, 14);
    qiLine(p, 14, 14, 27, 14);
    star(p, 27, 14, 3, QI);
  },
  // A strike on a meridian point inside the guard: the point bursts, the flow is cut.
  'sf-pulse': (p) => {
    p.path([
      [3, 24],
      [8, 18],
      [12, 14],
      [17, 11],
      [24, 6],
      [29, 4],
    ], PINE, 2);
    point(p, 14, 13, 4);
    pointing(p, 2, 3, true);
    for (const [x, y] of [
      [20, 14],
      [9, 9],
      [18, 6],
    ] as const)
      p.rect(x, y, 2, 1, QI);
  },
  // The brush flicked: many thin ink-qi lines fan out at once.
  'sf-six': (p) => {
    for (const a of [-0.7, -0.35, 0, 0.35, 0.7]) qiLine(p, 11, 18, 11 + Math.cos(a) * 18, 18 + Math.sin(a) * 15);
    brush(p, 2, 29, 11, 18, INK);
  },
  // The leg points closed from afar: a footstep pinned by a qi bead.
  'sf-hold': (p) => {
    footprint(p, 18, 15, PAPER);
    point(p, 21, 19, 5);
    qiLine(p, 2, 6, 17, 16);
    p.rect(13, 28, 14, 1, QI_DK);
  },
  // Plucked air like a qin: strings and the rings of their sound.
  'sf-qin': (p) => {
    p.poly([
      [4, 22],
      [26, 13],
      [28, 17],
      [6, 26],
    ], '#7a4a2a');
    for (let k = 0; k < 4; k++) p.line(6, 22 + k, 27, 13 + k, k % 2 ? '#d8b878' : PAPER);
    waves(p, 16, 12, 4, 3, QI_DK, 1.0, 3);
    p.ell(16, 12, 1.5, 1.5, QI);
  },
  // A scholar's stroll on the wind, a pine sprig carried along.
  'sf-drift': (p) => {
    sprite(p, 12, 5, ['..hh...', '..hh...', '.bbbb..', 'b.bbbb.', '..bbb.b', '..bbbb.', '.b...b.', '.b....b', 'b......'], { h: SKIN, b: PAPER }, { k: 2 });
    for (const [y, l] of [
      [12, 7],
      [17, 9],
      [22, 6],
    ] as const)
      p.rect(10 - l, y, l, 1, QI_DK);
    leaf(p, 26, 6, -1, 1, 5, PINE);
    leaf(p, 24, 4, 1, 1, 4, PINE);
  },
  // A mind like still water: one drop, one ring.
  'sf-water': (p) => {
    p.ring(17, 23, 12, 4, QI_DK, 0, Math.PI * 2);
    p.ring(17, 23, 7, 2, QI, 0, Math.PI * 2);
    p.poly([
      [17, 7],
      [14, 13],
      [17, 16],
      [20, 13],
    ], QI);
    p.rect(16, 9, 1, 3, C.white);
    leaf(p, 5, 6, 1, 1, 6, PINE);
  },
  // Two columns of qi-script written in the air; every stroke flies out.
  'sf-divine': (p) => {
    for (const x of [7, 13]) {
      inkStroke(p, [
        [x, 5],
        [x + 3, 6],
      ], 2, PAPER);
      inkStroke(p, [
        [x + 1, 9],
        [x + 2, 15],
      ], 2, PAPER);
      inkStroke(p, [
        [x - 1, 18],
        [x + 3, 17],
        [x + 1, 24],
      ], 2, PAPER);
    }
    for (const y of [8, 14, 20, 26]) qiLine(p, 19, y, 28, y - 3);
  },
});
